import { useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"
import { Download, FileSpreadsheet, Users2, AlertTriangle, CreditCard, Search, X, Calendar } from "lucide-react"
import * as XLSX from "xlsx"

import * as api from "@/api/cogeser"
import { useAuth } from "@/hooks/useAuth"

// ----------------------------------------------------------------------------

function formatDateForFilename(date: string) {
  return date.replace(/-/g, "")
}

function downloadExcel(data: Record<string, unknown>[], filename: string) {
  const ws = XLSX.utils.json_to_sheet(data)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, "Report")
  XLSX.writeFile(wb, filename)
}

// ----------------------------------------------------------------------------

export default function Reports() {
  // Hooks
  const { token } = useAuth()
  const now = new Date()
  const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`
  const today = now.toISOString().split("T")[0]
  const [dateFrom, setDateFrom] = useState(firstOfMonth)
  const [dateTo, setDateTo] = useState(today)

  // Non-conforming data download
  const nonConformingMutation = useMutation({
    mutationFn: () =>
      api.getReportNonConformingData(token!) as Promise<{ data: any[]; count: number }>,
    onSuccess: (result) => {
      if (!result.data.length) {
        toast.info("Nessun dato non conforme trovato")
        return
      }

      const rows = result.data.map((r: any) => ({
        "Codice Cliente": r.customerCode,
        "Codice Fiscale": r.fiscalCode,
        "Nome": r.name,
        "Cognome": r.surname,
        "Email DB": r.dbEmail || "",
        "Email Gestionale": r.erpEmail || "",
        "Email Diversa": r.emailMismatch ? "SÌ" : "",
        "Telefono DB": r.dbPhone || "",
        "Telefono Gestionale": r.erpPhone || "",
        "Telefono Diverso": r.phoneMismatch ? "SÌ" : "",
        "Email Registrazione": r.registeredEmail || "",
      }))

      const filename = `dati-non-conformi-${formatDateForFilename(dateFrom)}-${formatDateForFilename(dateTo)}.xlsx`
      downloadExcel(rows, filename)
      toast.success(`Report scaricato: ${result.data.length} record`)
    },
    onError: () => toast.error("Errore nel download del report"),
  })

  // Referrals download
  const referralsMutation = useMutation({
    mutationFn: () =>
      api.getReportReferrals(
        { from: dateFrom, to: dateTo },
        token!
      ) as Promise<{ data: any[]; count: number }>,
    onSuccess: (result) => {
      if (!result.data.length) {
        toast.info("Nessun referral trovato per il periodo selezionato")
        return
      }

      const rows = result.data.map((r: any) => ({
        "Data": r.date ? new Date(r.date).toLocaleDateString("it-IT") : "",
        "Codice Referrer": r.referrerCustomerCode || "",
        "Nome Referrer": r.referrerName || "",
        "Cognome Referrer": r.referrerSurname || "",
        "Codice Referral": r.referrerReferralCode || "",
        "Nome Invitato": r.redeemerName || "",
        "Cognome Invitato": r.redeemerSurname || "",
        "Codice Contratto": r.contractCode || "",
        "Punti": r.points,
        "Descrizione": r.description || "",
      }))

      const filename = `porta-un-amico-${formatDateForFilename(dateFrom)}-${formatDateForFilename(dateTo)}.xlsx`
      downloadExcel(rows, filename)
      toast.success(`Report scaricato: ${result.data.length} referral`)
    },
    onError: () => toast.error("Errore nel download del report"),
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Report</h1>
        <p className="mt-1 text-sm text-gray-500">
          Scarica i report in formato Excel per il periodo selezionato
        </p>
      </div>

      {/* Date Range Selector */}
      <div className="flex flex-wrap items-center gap-3">
        <Calendar className="h-4 w-4 text-gray-400" />
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Da:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">A:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      {/* Report Cards */}
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Loyalty transactions export */}
        <LoyaltyTransactionsCard
          token={token!}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />

        {/* Non-conforming data */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-orange-50">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900">Dati Non Conformi</h3>
              <p className="mt-1 text-sm text-gray-500">
                Clienti con email o telefono diversi rispetto ai dati del gestionale Cogeser.
                Per bonifica dati manuale.
              </p>
              <button
                onClick={() => nonConformingMutation.mutate()}
                disabled={nonConformingMutation.isPending}
                className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {nonConformingMutation.isPending ? (
                  <>
                    <FileSpreadsheet className="h-4 w-4 animate-pulse" />
                    Generazione...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Scarica Excel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Porta un Amico */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-blue-50">
              <Users2 className="h-6 w-6 text-blue-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900">Porta un Amico</h3>
              <p className="mt-1 text-sm text-gray-500">
                Report degli utenti che hanno utilizzato la funzionalità "Porta un amico"
                nel periodo selezionato.
              </p>
              <button
                onClick={() => referralsMutation.mutate()}
                disabled={referralsMutation.isPending}
                className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {referralsMutation.isPending ? (
                  <>
                    <FileSpreadsheet className="h-4 w-4 animate-pulse" />
                    Generazione...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Scarica Excel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Loyalty Transactions Export Card
// ============================================================================

function LoyaltyTransactionsCard({
  token,
  dateFrom,
  dateTo,
}: {
  token: string
  dateFrom: string
  dateTo: string
}) {
  const [customerSearch, setCustomerSearch] = useState("")
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<{ id: string; label: string } | null>(null)
  const [isSearching, setIsSearching] = useState(false)

  async function handleSearch(query: string) {
    setCustomerSearch(query)
    if (query.length < 2) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      const result = await api.getCustomers({ search: query, limit: 8 }, token) as any
      setSearchResults(result?.data || [])
    } catch {
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  function selectCustomer(customer: any) {
    setSelectedCustomer({
      id: customer.id || customer._id,
      label: `${customer.surname || ""} ${customer.name || ""} (${customer.customerCode})`.trim(),
    })
    setCustomerSearch("")
    setSearchResults([])
  }

  function clearCustomer() {
    setSelectedCustomer(null)
  }

  const exportMutation = useMutation({
    mutationFn: () => {
      const params: Record<string, unknown> = {
        from: dateFrom,
        to: dateTo,
      }
      if (selectedCustomer) {
        params.customer = selectedCustomer.id
      }
      return api.getReportLoyaltyTransactions(params, token) as Promise<{ data: any[]; count: number }>
    },
    onSuccess: (result) => {
      if (!result.data.length) {
        toast.info("Nessuna movimentazione trovata per il periodo selezionato")
        return
      }

      const rows = result.data.map((r: any) => ({
        "Data": r.date ? new Date(r.date).toLocaleDateString("it-IT") : "",
        "Codice Cliente": r.customerCode || "",
        "Nome": r.customerName || "",
        "Cognome": r.customerSurname || "",
        "Codice Fiscale": r.fiscalCode || "",
        "Codice Regola": r.ruleCode || "",
        "Tipo Movimento": r.ruleLabel || "",
        "Punti": r.points,
        "Saldo Dopo": r.balanceAfter,
        "Descrizione": r.description || "",
        "Fonte": r.source || "",
        "Codice Referral": r.referralCode || "",
      }))

      const suffix = selectedCustomer ? `-cliente-${selectedCustomer.id.slice(-6)}` : ""
      const filename = `movimentazioni-fidelity-${formatDateForFilename(dateFrom)}-${formatDateForFilename(dateTo)}${suffix}.xlsx`
      downloadExcel(rows, filename)
      toast.success(`Report scaricato: ${result.data.length} movimentazioni`)
    },
    onError: () => toast.error("Errore nel download del report"),
  })

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-green-50">
          <CreditCard className="h-6 w-6 text-green-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900">Movimentazioni Fidelity</h3>
          <p className="mt-1 text-sm text-gray-500">
            Export di tutte le movimentazioni punti nel periodo selezionato.
            Filtra per cliente o scarica tutto.
          </p>

          {/* Customer filter */}
          <div className="mt-3 space-y-2">
            {selectedCustomer ? (
              <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                <Search className="h-4 w-4 text-primary" />
                <span className="flex-1 text-sm font-medium text-gray-900">{selectedCustomer.label}</span>
                <button onClick={clearCustomer} className="rounded p-0.5 text-gray-400 hover:text-gray-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Filtra per cliente (nome, cognome, codice)..."
                  className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-3 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
                {searchResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {searchResults.map((c: any) => (
                      <button
                        key={c.id || c._id}
                        onClick={() => selectCustomer(c)}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm hover:bg-gray-50"
                      >
                        <span className="font-medium text-gray-900">{c.surname} {c.name}</span>
                        <span className="text-xs text-gray-400">#{c.customerCode}</span>
                      </button>
                    ))}
                  </div>
                )}
                {isSearching && (
                  <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-lg border border-gray-200 bg-white p-3 text-center text-xs text-gray-500 shadow-lg">
                    Ricerca...
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-gray-400">
              {selectedCustomer ? "Solo le movimentazioni di questo cliente" : "Nessun filtro — verranno esportate tutte le movimentazioni"}
            </p>
          </div>

          <button
            onClick={() => exportMutation.mutate()}
            disabled={exportMutation.isPending}
            className="mt-4 flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {exportMutation.isPending ? (
              <>
                <FileSpreadsheet className="h-4 w-4 animate-pulse" />
                Generazione...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Scarica Excel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
